/**
 * Preview / Live Calendar view — Phase 2.3.5 (B)
 *
 * Week grid, 30-min slots, read-only. Clearly labels Preview vs Live.
 * Shows booked events, held slots, and empty (available) slots.
 *
 * Data: GET /api/channels/{id}/calendar/events?start=..&end=..
 *       GET /api/channels/{id}/calendar/status
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../components/ui/hover-card';
import { Calendar as CalIcon, ChevronLeft, ChevronRight, Eye, Zap, RefreshCw, Info, Link2, FlaskConical, User, Phone, FileText, Clock, CheckCircle, Hash, Trash2 } from 'lucide-react';
import { API, useAuth } from '../../App';
import { useDemoMode } from '../../contexts/DemoModeContext';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { Link } from 'react-router-dom';

const DAY_LABELS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const DAY_FULL = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

// ---- date helpers ----
const startOfWeek = (d) => {
  const x = new Date(d); x.setHours(0,0,0,0);
  x.setDate(x.getDate() - x.getDay()); // Sunday start
  return x;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const toIso = (d) => d.toISOString();
const fmtDate = (d) => `${DAY_LABELS[d.getDay()]} ${d.getDate()}/${d.getMonth()+1}`;
const fmtTime = (d) => `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
const parseIso = (s) => new Date(s);

// Grid: 30-min slots from 08:00 to 20:00 (24 rows)
const SLOT_MIN = 30;
const HOURS_START = 8;
const HOURS_END = 20;
const ROWS_PER_DAY = ((HOURS_END - HOURS_START) * 60) / SLOT_MIN; // 24

const CalendarPage = () => {
  const { token, user } = useAuth();
  const authHeaders = { Authorization: `Bearer ${token}` };
  const demo = useDemoMode();

  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [holds, setHolds] = useState([]);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [loading, setLoading] = useState(true);
  const [newlyBookedId, setNewlyBookedId] = useState(null);
  const [purging, setPurging] = useState(false);
  const [purgeScope, setPurgeScope] = useState('cancelled');
  const [purgeOpen, setPurgeOpen] = useState(false);
  const prevEventIdsRef = useRef(new Set());

  // Load channels on mount — prefer demo channel when Demo Mode is active
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/channels`, { headers: authHeaders });
        const list = res.data?.channels || res.data || [];
        setChannels(list);
        if (list.length && !selectedChannel) {
          const demoIds = new Set(demo?.demoChannelIds || []);
          const preferred = demo?.active
            ? list.find(c => demoIds.has(c.id) || c.is_demo)
            : null;
          setSelectedChannel((preferred || list[0]).id);
        }
      } catch (err) {
        console.error('[CALENDAR] channels fetch failed', err);
        toast.error('Failed to load channels');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line
  }, [demo?.active, demo?.initialized]);

  // When Demo Mode flips on/off, realign selection to the right channel cohort
  useEffect(() => {
    if (!channels.length || !demo?.initialized) return;
    const demoIds = new Set(demo?.demoChannelIds || []);
    const currentIsDemo = channels.find(c => c.id === selectedChannel)?.is_demo
      || demoIds.has(selectedChannel);
    if (demo?.active && !currentIsDemo) {
      const next = channels.find(c => demoIds.has(c.id) || c.is_demo);
      if (next) setSelectedChannel(next.id);
    } else if (!demo?.active && currentIsDemo) {
      const next = channels.find(c => !c.is_demo && !demoIds.has(c.id));
      if (next) setSelectedChannel(next.id);
    }
    // eslint-disable-next-line
  }, [demo?.active, demo?.initialized, channels.length]);

  const fetchStatus = useCallback(async (ch) => {
    if (!ch) return;
    try {
      const res = await axios.get(`${API}/channels/${ch}/calendar/status`, { headers: authHeaders });
      setStatus(res.data);
    } catch (err) {
      console.error('[CALENDAR] status fetch failed', err);
    }
    // eslint-disable-next-line
  }, [token]);

  const handlePurge = useCallback(async () => {
    if (!selectedChannel) return;
    setPurging(true);
    try {
      const res = await axios.post(
        `${API}/channels/${selectedChannel}/calendar/simulated/purge?scope=${encodeURIComponent(purgeScope)}`,
        {},
        { headers: authHeaders }
      );
      const { removed_count = 0, kept_count = 0 } = res.data || {};
      toast.success(`Purged ${removed_count} simulated event(s). ${kept_count} kept.`);
      setPurgeOpen(false);
      fetchEvents(selectedChannel, weekStart);
    } catch (err) {
      console.error('[CALENDAR] purge failed', err);
      const msg = err?.response?.data?.detail || 'Purge failed';
      toast.error(msg);
    } finally {
      setPurging(false);
    }
    // eslint-disable-next-line
  }, [selectedChannel, purgeScope, weekStart, token]);

  const fetchEvents = useCallback(async (ch, ws, { silent = false } = {}) => {
    if (!ch) return;
    const start = toIso(ws);
    const end = toIso(addDays(ws, 7));
    try {
      const res = await axios.get(
        `${API}/channels/${ch}/calendar/events?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
        { headers: authHeaders }
      );
      const newEvents = res.data?.events || [];
      // Detect new bookings for live-update highlight + toast
      const prevIds = prevEventIdsRef.current;
      const currentIds = new Set(newEvents.map(e => e.event_id));
      if (!silent) {
        for (const e of newEvents) {
          if (!prevIds.has(e.event_id)) {
            setNewlyBookedId(e.event_id);
            toast.success(`New booking: ${e.summary || e.event_id}`);
            setTimeout(() => setNewlyBookedId(null), 6000);
            break; // highlight one at a time
          }
        }
      }
      prevEventIdsRef.current = currentIds;
      setEvents(newEvents);
      setHolds(res.data?.holds || []);
    } catch (err) {
      console.error('[CALENDAR] events fetch failed', err);
    }
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    if (selectedChannel) {
      // initial load — suppress "new booking" toast
      prevEventIdsRef.current = new Set();
      fetchStatus(selectedChannel);
      fetchEvents(selectedChannel, weekStart, { silent: true });
    }
  }, [selectedChannel, weekStart, fetchStatus, fetchEvents]);

  // Live refresh — poll every 8s when a channel is selected so new bookings
  // appear automatically.
  useEffect(() => {
    if (!selectedChannel) return;
    const id = setInterval(() => {
      fetchEvents(selectedChannel, weekStart);
    }, 8000);
    return () => clearInterval(id);
  }, [selectedChannel, weekStart, fetchEvents]);

  // ---- TZ-aware part extraction ----
  // Uses the channel's configured timezone (IANA string) so events render on the
  // correct day/row regardless of the browser's local tz. If the configured
  // value is missing or invalid (e.g. "GMT+8" instead of "Asia/Kuala_Lumpur"),
  // silently falls back to the browser's local time so the grid never breaks.
  const channelTz = useMemo(() => {
    const tz = status?.timezone || '';
    try {
      // Test the tz — throws on invalid
      new Intl.DateTimeFormat('en-US', { timeZone: tz }).format(new Date());
      return tz;
    } catch {
      return undefined; // fall back to local
    }
  }, [status?.timezone]);

  const tzParts = useCallback((iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    try {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: channelTz,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
      }).formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
      return {
        year:   parseInt(parts.year, 10),
        month:  parseInt(parts.month, 10),
        day:    parseInt(parts.day, 10),
        // "24" is a legitimate output for 00:00 in some locales
        hour:   parseInt(parts.hour, 10) % 24,
        minute: parseInt(parts.minute, 10),
      };
    } catch {
      return {
        year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate(),
        hour: d.getHours(), minute: d.getMinutes(),
      };
    }
  }, [channelTz]);

  // Build grid: days x rows
  const days = useMemo(() => Array.from({length: 7}, (_, i) => addDays(weekStart, i)), [weekStart]);

  const slotAt = (day, row) => {
    const d = new Date(day);
    const minutes = HOURS_START * 60 + row * SLOT_MIN;
    d.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
    return d;
  };

  // Indexers — find which slot(s) an event/hold spans (channel-tz aware)
  const grid = useMemo(() => {
    const cells = Array.from({length: ROWS_PER_DAY}, () => Array.from({length: 7}, () => ({events: [], holds: []})));
    const evtsAll = [...events.map(e => ({...e, _type: 'event'})), ...holds.map(h => ({
      event_id: h.hold_id, summary: `Held (conv ${String(h.conversation_id).slice(0,6)}…)`,
      start: h.slot_start_iso, end: h.slot_end_iso, _type: 'hold',
    }))];
    for (const e of evtsAll) {
      const sp = tzParts(e.start); const ep = tzParts(e.end);
      if (!sp || !ep) continue;
      for (let d = 0; d < 7; d++) {
        const day = days[d];
        // `day` is in the browser's local tz midnight — compare Y/M/D calendar parts
        if (sp.year !== day.getFullYear() || sp.month !== (day.getMonth() + 1) || sp.day !== day.getDate()) continue;
        const startMin = sp.hour * 60 + sp.minute;
        const endMin   = ep.hour * 60 + ep.minute;
        const startRow = Math.max(0, Math.floor((startMin - HOURS_START * 60) / SLOT_MIN));
        const endRow   = Math.min(ROWS_PER_DAY, Math.ceil((endMin   - HOURS_START * 60) / SLOT_MIN));
        for (let r = startRow; r < endRow; r++) {
          if (!cells[r] || !cells[r][d]) continue;
          if (e._type === 'event') cells[r][d].events.push(e);
          else cells[r][d].holds.push(e);
        }
      }
    }
    return cells;
  }, [events, holds, days, tzParts]);

  // Detect events that exist but don't land in the visible week (so the user
  // sees "1 booked event" in footer but an empty grid) — offer a jump.
  const offscreenEvents = useMemo(() => {
    const weekStartMs = weekStart.getTime();
    const weekEndMs = addDays(weekStart, 7).getTime();
    const off = [];
    for (const e of events) {
      const p = tzParts(e.start);
      if (!p) continue;
      const eventLocalMid = new Date(p.year, p.month - 1, p.day).getTime();
      if (eventLocalMid < weekStartMs || eventLocalMid >= weekEndMs) {
        off.push({ event: e, localDate: new Date(p.year, p.month - 1, p.day) });
      }
    }
    return off;
  }, [events, weekStart, tzParts]);

  const preview = status?.preview_active;
  const provider = status?.provider || 'none';
  // Live Google connected — suppress the empty internal grid (Google
  // owns the calendar; the grid would just look broken). Show a clean
  // "connected" state instead. Simulated/demo/preview keep the grid.
  const isLiveGoogle = !preview && !!status?.connected && provider === 'google';

  if (loading) {
    return <div className="p-8"><RefreshCw className="h-5 w-5 animate-spin" /></div>;
  }

  if (!channels.length) {
    return (
      <div className="p-8 space-y-4" data-testid="calendar-page">
        <h1 className="text-2xl font-semibold">Calendar</h1>
        <Alert><Info className="h-4 w-4" /><AlertDescription>
          No channels yet. Create a channel first on the <Link to="/dashboard/channels" className="underline">Channels</Link> page, then connect or preview-enable calendar there.
        </AlertDescription></Alert>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="calendar-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <CalIcon className="h-6 w-6 text-blue-600" /> Calendar
            </h1>
            {preview ? (
              <Badge className="bg-amber-100 text-amber-800 border border-amber-300" data-testid="calendar-mode-preview">
                <FlaskConical className="h-3 w-3 mr-1" /> {demo.active ? 'Demo Calendar' : 'Preview Calendar'}
              </Badge>
            ) : status?.connected ? (
              <Badge className="bg-emerald-600" data-testid="calendar-mode-live">
                <Zap className="h-3 w-3 mr-1" /> Live Calendar
              </Badge>
            ) : demo.active ? (
              <Badge className="bg-amber-100 text-amber-800 border border-amber-300" data-testid="calendar-mode-demo">
                <FlaskConical className="h-3 w-3 mr-1" /> Demo Calendar
              </Badge>
            ) : (
              <Badge variant="outline" data-testid="calendar-mode-unconfigured">Not connected</Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Week view · 30-min slots · {
              demo.active
                ? 'Using demo data — no live calendar will be touched.'
                : preview
                  ? 'Preview mode — no live calendar will be touched.'
                  : status?.connected
                    ? `Connected to ${status.connected_email}`
                    : 'No calendar connected for this channel.'
            }
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedChannel || ''} onValueChange={setSelectedChannel}>
            <SelectTrigger className="w-[240px]" data-testid="calendar-channel-select">
              <SelectValue placeholder="Select channel" />
            </SelectTrigger>
            <SelectContent>
              {channels.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name || c.phone_number || c.id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="calendar-prev-week"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" onClick={() => setWeekStart(startOfWeek(new Date()))} data-testid="calendar-this-week">This week</Button>
          <Button variant="outline" size="icon" onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="calendar-next-week"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="icon" onClick={() => { fetchStatus(selectedChannel); fetchEvents(selectedChannel, weekStart); }} data-testid="calendar-refresh"><RefreshCw className="h-4 w-4" /></Button>
          {preview && (
            <AlertDialog open={purgeOpen} onOpenChange={setPurgeOpen}>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-amber-700 border-amber-300 hover:bg-amber-50"
                  data-testid="calendar-purge-sim-btn"
                >
                  <Trash2 className="h-4 w-4 mr-1.5" />
                  Purge demo events
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="calendar-purge-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Purge simulated calendar events?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This clears entries from the <b>simulated</b> calendar store for this
                    channel only. It <b>never</b> touches a real Google Calendar or any
                    live provider events. Choose what to remove:
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="space-y-2 py-2">
                  {[
                    { v: 'cancelled', label: 'Cancelled events only', hint: 'Safe cleanup — removes tombstoned entries.' },
                    { v: 'past',      label: 'Past-dated events',     hint: 'Removes events whose end time is already in the past.' },
                    { v: 'all',       label: 'All simulated events',  hint: 'Wipes the entire simulated store for this channel.' },
                  ].map(opt => (
                    <label
                      key={opt.v}
                      className={`flex items-start gap-2 rounded-md border p-2 cursor-pointer hover:bg-muted/40 ${purgeScope === opt.v ? 'border-amber-500 bg-amber-50' : ''}`}
                      data-testid={`calendar-purge-scope-${opt.v}`}
                    >
                      <input
                        type="radio"
                        name="purge-scope"
                        className="mt-1"
                        checked={purgeScope === opt.v}
                        onChange={() => setPurgeScope(opt.v)}
                      />
                      <div>
                        <div className="text-sm font-medium">{opt.label}</div>
                        <div className="text-xs text-muted-foreground">{opt.hint}</div>
                      </div>
                    </label>
                  ))}
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="calendar-purge-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={(e) => { e.preventDefault(); handlePurge(); }}
                    disabled={purging}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="calendar-purge-confirm"
                  >
                    {purging ? 'Purging…' : 'Purge'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Live Google clean state — Google owns the calendar; suppress the
          empty internal grid and show a connected card with a deep-link to
          Google Calendar. Simulated/demo/preview/not-connected keep the
          existing grid below. */}
      {isLiveGoogle && (
        <Card data-testid="calendar-live-google-card">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <Zap className="h-5 w-5 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-semibold" data-testid="calendar-live-google-title">
                  Live Google Calendar connected
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Bookings created via WhatsApp appear directly in Google Calendar.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {status?.connected_email && (
                <div className="rounded-md border p-3" data-testid="calendar-live-google-email">
                  <div className="text-xs text-muted-foreground">Connected account</div>
                  <div className="font-medium truncate">{status.connected_email}</div>
                </div>
              )}
              {status?.calendar_id && (
                <div className="rounded-md border p-3" data-testid="calendar-live-google-calendar-id">
                  <div className="text-xs text-muted-foreground">Calendar</div>
                  <div className="font-mono text-xs truncate">{status.calendar_id}</div>
                </div>
              )}
              {status?.timezone && (
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground">Timezone</div>
                  <div className="font-medium truncate">{status.timezone}</div>
                </div>
              )}
              {status?.booking_mode && (
                <div className="rounded-md border p-3">
                  <div className="text-xs text-muted-foreground">Booking mode</div>
                  <div className="font-medium capitalize">{String(status.booking_mode).replace(/_/g, ' ')}</div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 pt-2">
              <a
                href="https://calendar.google.com/calendar/u/0/r"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="calendar-open-google-btn"
              >
                <Button variant="default" className="bg-emerald-600 hover:bg-emerald-700">
                  <Link2 className="h-4 w-4 mr-1.5" />
                  Open Google Calendar
                </Button>
              </a>
              <Button
                variant="outline"
                onClick={() => { fetchStatus(selectedChannel); fetchEvents(selectedChannel, weekStart); }}
                data-testid="calendar-live-google-refresh"
              >
                <RefreshCw className="h-4 w-4 mr-1.5" />
                Refresh status
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Legend */}
      {!isLiveGoogle && (<>
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-blue-500" />Booked</span>
        <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-amber-300 border border-amber-500" />Held</span>
        <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded bg-muted border" />Available</span>
        <span className="ml-auto">Range: {fmtDate(weekStart)} → {fmtDate(addDays(weekStart, 6))}</span>
      </div>

      {/* Off-screen events banner — event exists but not in visible week */}
      {events.length > 0 && offscreenEvents.length === events.length && (
        <div
          className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded p-2 flex items-center justify-between gap-2"
          data-testid="calendar-offscreen-banner"
        >
          <span>
            <Info className="inline h-4 w-4 mr-1" />
            {events.length} booked event(s) exist outside this week.
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setWeekStart(startOfWeek(offscreenEvents[0].localDate))}
            data-testid="calendar-jump-to-event"
          >
            Jump to first event
          </Button>
        </div>
      )}

      {/* Demo hint */}
      {demo.active && (
        <div
          className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2"
          data-testid="calendar-demo-hint"
        >
          <FlaskConical className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <span>
            <b>Try it:</b> open a conversation, approve a booking, and it will appear here live. Held slots show up the moment a customer proposes a time.
          </span>
        </div>
      )}

      {/* Grid */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <div className="grid border-b text-xs font-medium bg-muted/40"
               style={{gridTemplateColumns: '64px repeat(7, minmax(120px, 1fr))'}}>
            <div className="p-2">Time</div>
            {days.map((d, i) => (
              <div key={i} className="p-2 border-l text-center">
                <div>{DAY_LABELS[d.getDay()]}</div>
                <div className="text-muted-foreground">{d.getDate()}/{d.getMonth()+1}</div>
              </div>
            ))}
          </div>
          {Array.from({length: ROWS_PER_DAY}, (_, row) => {
            const slotMinutes = HOURS_START * 60 + row * SLOT_MIN;
            const label = `${String(Math.floor(slotMinutes/60)).padStart(2,'0')}:${String(slotMinutes%60).padStart(2,'0')}`;
            return (
              <div key={row} className="grid border-b text-xs"
                   style={{gridTemplateColumns: '64px repeat(7, minmax(120px, 1fr))'}}>
                <div className="p-1.5 text-muted-foreground border-r bg-muted/20">{label}</div>
                {days.map((d, di) => {
                  const cell = grid[row][di];
                  const hasEvent = cell.events.length > 0;
                  const hasHold = cell.holds.length > 0;
                  const isNewlyBooked = hasEvent && cell.events.some(e => e.event_id === newlyBookedId);
                  const bg = hasEvent
                    ? (isNewlyBooked
                        ? 'bg-emerald-500 text-white ring-2 ring-emerald-300 animate-pulse'
                        : 'bg-blue-500/90 text-white')
                    : hasHold ? 'bg-amber-200 border-amber-400' : '';
                  const tid = hasEvent ? 'calendar-cell-booked' : hasHold ? 'calendar-cell-held' : 'calendar-cell-available';
                  const ev0 = hasEvent ? cell.events[0] : null;
                  const isStartRow = ev0 ? (() => {
                    const ep = tzParts(ev0.start);
                    if (!ep) return false;
                    const s = Math.max(0, Math.floor((ep.hour * 60 + ep.minute - HOURS_START * 60) / SLOT_MIN));
                    return row === s;
                  })() : false;

                  // Cell content for booked slots: prefer customer_name + purpose
                  // over the raw summary, so the grid actually communicates
                  // what the booking is for.
                  const renderEventBody = () => {
                    if (!ev0 || !isStartRow) return null;
                    const name = ev0.customer_name || ev0.summary || '—';
                    const purpose = ev0.purpose_for_visit || '';
                    return (
                      <div className="leading-tight overflow-hidden">
                        <div className="text-[11px] font-semibold truncate" data-testid="calendar-cell-name">{name}</div>
                        {purpose && (
                          <div className="text-[10px] opacity-90 truncate" data-testid="calendar-cell-purpose">{purpose}</div>
                        )}
                      </div>
                    );
                  };

                  const cellInner = (
                    <div data-testid={tid}
                         className={`p-1 border-l min-h-[28px] ${bg} ${hasEvent ? 'cursor-pointer hover:brightness-110' : ''}`}>
                      {renderEventBody()}
                    </div>
                  );

                  // Plain (no hover) for holds/empty cells; HoverCard for bookings
                  if (!hasEvent) {
                    const title = hasHold ? cell.holds.map(h=>h.summary).join('\n') : 'Available';
                    return (
                      <div key={di} title={title} data-testid={tid}
                           className={`p-1 border-l min-h-[28px] ${bg}`} />
                    );
                  }

                  // Format time in channel-tz so the hover card matches the
                  // grid cell position (the grid uses tzParts which is
                  // channel-tz aware).
                  const fmtChTime = (iso) => {
                    const p = tzParts(iso);
                    if (!p) return fmtTime(parseIso(iso));
                    return `${String(p.hour).padStart(2,'0')}:${String(p.minute).padStart(2,'0')}`;
                  };

                  return (
                    <HoverCard key={di} openDelay={120} closeDelay={80}>
                      <HoverCardTrigger asChild>
                        {cellInner}
                      </HoverCardTrigger>
                      <HoverCardContent
                        side="right" align="start" sideOffset={6}
                        className="w-80 p-0 overflow-hidden"
                        data-testid="calendar-event-hover"
                      >
                        <div className="bg-blue-500 text-white px-3 py-2">
                          <div className="text-sm font-semibold truncate">
                            {ev0.customer_name || ev0.summary || 'Booking'}
                          </div>
                          <div className="text-[11px] opacity-90 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {fmtChTime(ev0.start)} – {fmtChTime(ev0.end)}
                            <span className="opacity-60 ml-1">·</span>
                            <span className="opacity-90">{DAY_FULL[d.getDay()]}, {d.getDate()}/{d.getMonth()+1}</span>
                          </div>
                        </div>
                        <div className="p-3 space-y-1.5 text-xs">
                          {ev0.purpose_for_visit && (
                            <div className="flex items-start gap-2">
                              <FileText className="h-3.5 w-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                              <div><span className="text-muted-foreground">Purpose: </span><span className="font-medium">{ev0.purpose_for_visit}</span></div>
                            </div>
                          )}
                          {ev0.customer_phone && (
                            <div className="flex items-start gap-2">
                              <Phone className="h-3.5 w-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                              <div><span className="text-muted-foreground">Phone: </span><span className="font-medium tabular-nums">{ev0.customer_phone}</span></div>
                            </div>
                          )}
                          {ev0.customer_name && (
                            <div className="flex items-start gap-2">
                              <User className="h-3.5 w-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                              <div><span className="text-muted-foreground">Customer: </span><span className="font-medium">{ev0.customer_name}</span></div>
                            </div>
                          )}
                          <div className="flex items-start gap-2">
                            <CheckCircle className="h-3.5 w-3.5 mt-0.5 text-emerald-600 flex-shrink-0" />
                            <div>
                              <span className="text-muted-foreground">Booking: </span>
                              <Badge variant="outline" className="border-emerald-300 text-emerald-700 text-[10px] px-1.5 py-0">{ev0.booking_status || ev0.status || 'confirmed'}</Badge>
                              <span className="text-muted-foreground ml-1.5">Sync: </span>
                              <Badge variant="outline" className="border-blue-300 text-blue-700 text-[10px] px-1.5 py-0">{ev0.source || 'simulated'}</Badge>
                            </div>
                          </div>
                          {ev0.conversation_id && (
                            <div className="flex items-start gap-2 pt-1.5 border-t mt-2">
                              <Hash className="h-3.5 w-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                              <Link
                                to={`/dashboard/conversations?focus=${ev0.conversation_id}`}
                                className="text-blue-600 hover:underline truncate font-mono text-[10px]"
                                data-testid="calendar-event-conv-link"
                              >
                                {String(ev0.conversation_id).slice(0, 8)}…
                              </Link>
                            </div>
                          )}
                          {ev0.intake_summary && (
                            <div className="text-[10px] text-muted-foreground pt-1.5 border-t mt-2 italic line-clamp-3">
                              {ev0.intake_summary}
                            </div>
                          )}
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  );
                })}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Footer stats */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span><b className="text-foreground">{events.length}</b> booked event(s)</span>
        <span><b className="text-foreground">{holds.length}</b> held slot(s)</span>
        <span className="ml-auto flex items-center gap-2">Provider: <Badge variant="outline">{provider}</Badge></span>
      </div>
      </>)}
    </div>
  );
};

export default CalendarPage;
